<script>
  import { page, navigating } from "$app/stores";
  import Icon from "$lib/Icon.svelte";
  import "../app.css";
  import { fade, grow } from "$lib/transitions.js";
  import layout from "$lib/layout.js";
  import Text from "$lib/Text.svelte";
  import Headlines from "$lib/Blocks/Headlines.svelte";
  import Subnav from "$lib/Subnav.svelte";
  import { slide } from "svelte/transition";
  import { onMount } from "svelte";
  import { browser } from "$app/environment";
  import { linkResolver } from "$lib/prismic";

  export let data;

  let consent = null;
  $: ({ global, preview, dnt } = data);

  $: consent = data.consent;

  let nav = false;

  $: path = $page.url.pathname;

  let scrolled = false;
  let scrollTimer = null;
  let scrollY = null;
  const scroll = () => {
    window.requestAnimationFrame(() => {
      scrolled = window.scrollY > 5;
      if (scrollTimer) clearTimeout(scrollTimer);
      if (scrollY !== null && Math.abs(window.scrollY - scrollY) > 10) {
        subnav = null;
      }
      scrollY = window.scrollY;
      scrollTimer = setTimeout(() => {
        scrollY = null;
      }, 100);
    });
  };

  let left = 0;
  let width = 0;
  const hover = (target) => {
    if (!target) {
      width = 0;
    } else {
      left = target.offsetLeft;
      width = target.offsetWidth;
    }
  };

  let navItem = [];

  $: activeNav =
    navItem[
      global.nav.findIndex(({ page }) => {
        return path.indexOf(linkResolver(page._meta)) === 0;
      })
    ];

  $: if (activeNav) {
    hover(activeNav);
  } else {
    hover();
  }

  const cookies = async (c) => {
    const Cookies = await import("js-cookie");
    consent = c;
    Cookies.set("consent", c, { expires: 365 });
  };

  let subnav = null;

  let nBox = null;
  const navBox = (el) => {
    nBox = el.getBoundingClientRect();
    const resizeObserver = new ResizeObserver(() => {
      nBox = el.getBoundingClientRect();
    });
    resizeObserver.observe(el);
    return {
      destroy() {
        resizeObserver.unobserve(el);
      },
    };
  };

  onMount(() => {
    if (consent && global.google_analytics && !dnt) {
      (function (w, d, s, l, i) {
        w[l] = w[l] || [];
        w[l].push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
        var f = d.getElementsByTagName(s)[0],
          j = d.createElement(s),
          dl = l != "dataLayer" ? "&l=" + l : "";
        j.async = true;
        j.src = "https://www.googletagmanager.com/gtm.js?id=" + i + dl;
        f.parentNode.insertBefore(j, f);
      })(window, document, "script", "dataLayer", global.google_analytics);
    }
  });

  $: subnavCols = ["col_1", "col_2", "col_3"].flatMap((col) => {
    const headline = subnav?.[col];
    if (!headline) return [];
    return {
      headline,
      pages: subnav.subnav.filter(
        ({ page, title_overwrite, column: pageColumn }) => pageColumn === col,
      ),
    };
  });
</script>

<!-- svelte-ignore a11y_no_static_element_interactions -->
<!-- svelte-ignore a11y_click_events_have_key_events -->
<svelte:head>
  <link
    rel="icon"
    type="image/png"
    href="/favicon.png"
    media="(prefers-color-scheme: light)"
  />
  <link
    rel="icon"
    type="image/png"
    href="/favicon-dark.png"
    media="(prefers-color-scheme: dark)"
  />
  <link rel="icon" type="image/svg+xml" href="/favicon.svg" />
  <link rel="mask-icon" href="/favicon.svg" color="#202020" />

  {#if preview}
    <script
      async
      defer
      src="https://static.cdn.prismic.io/prismic.js?new=true&repo=proxenos"
    ></script>
  {/if}
  <script
    src="https://cdn.usefathom.com/script.js"
    data-site="OAZQEVTY"
    data-spa="auto"
    defer
  ></script>
</svelte:head>
<svelte:window on:scroll={scroll} />

<!-- svelte-ignore a11y_click_events_have_key_events -->
<!-- svelte-ignore a11y_no_static_element_interactions -->
<div class="flex flex-col min-h-screen light" on:click={() => (subnav = null)}>
  {#if $navigating}
    <!-- svelte-ignore element_invalid_self_closing_tag -->
    <div
      out:grow|global
      in:fade|global
      class="fixed inset-x-0 top-0 z-50 gradient h-3px transform scale-x-0"
    />
  {/if}
  <header
    class="{!$layout.hero || scrolled || subnav
      ? 'bg-darkgray border-darkgray'
      : 'border-transparent'} fixed top-0 inset-x-0 px-8 border-b-3 text-gray z-20 {browser
      ? 'transition-all duration-300'
      : ''} flex items-center h-20"
  >
    <div
      class="max-w-content mx-auto flex flex-wrap items-center justify-between w-full"
    >
      <a href="/" class="relative z-10 block my-auto">
        <img src="/logo.svg" class="block w-16" alt="Hertz6 Logo" />
      </a>
      <nav
        use:navBox
        class="overflow-y-auto lg:overflow-y-visible pt-[16rem] pb-16 lg:py-0 px-4 lg:p-0 h-full fixed font-display inset-0 lg:inset-auto bg-darkgray lg:opacity-100 lg:pointer-events-auto lg:transition-none backdrop-filter backdrop-blur-sm lg:backdrop-filter-none bg-opacity-90 lg:bg-transparent lg:relative flex flex-col lg:flex-row lg:items-center justify-center landscape:justify-end lg:justify-center font-semibold uppercase text-3xl landscape:text-lg lg:text-lg xl:text-3xl {browser
          ? 'transition duration-500'
          : ''} {nav
          ? 'pointer-events-auto opacity-100'
          : 'pointer-events-none opacity-0'}"
      >
        <span
          class="hidden lg:block absolute left-0 inset-y-0 transition duration-300 origin-left border-0"
          style="width: 1000px; transform: translateX({left}px) translateZ(0) scaleX({width /
            1000});"><span class="block absolute inset-0 gradient" /></span
        >
        {#each global.nav as { page }, index}
          <Subnav
            {page}
            bind:nav
            item={navItem[index]}
            {path}
            {hover}
            {activeNav}
            bind:subnav
          />
        {/each}
      </nav>
      <!-- svelte-ignore a11y_consider_explicit_label -->
      <button
        class="block p-0 overflow-hidden nav-button lg:hidden focus:outline-none shadow-none relative {nav
          ? 'active'
          : ''}"
        on:click={() => (nav = !nav)}><span /></button
      >
    </div>
    {#if subnav}
      <!-- svelte-ignore a11y-mouse-events-have-key-events -->
      <div
        transition:slide|global={{ axis: "y" }}
        id="subnav"
        class="hidden lg:block absolute inset-x-0 top-full bg-darkgray/90"
        style="padding-left: {nBox.left}px; padding-right: {window.innerWidth -
          nBox.right}px"
        on:mouseleave={() => (subnav = null)}
      >
        <div class="p-8">
          <a
            href={linkResolver(subnav._meta)}
            class="text-3xl font-display hover:text-white/50 transition-colors"
            >{subnav.overview_label || "Übersicht"}</a
          >
          <div
            class="grid gap-4 mt-4 {subnavCols.length > 2
              ? 'grid-cols-3'
              : 'grid-cols-2'}"
          >
            {#each subnavCols as { headline, pages }}
              <div>
                <span
                  class="block text-2xl uppercase font-display font-light text-white/50 mb-2"
                  >{headline}</span
                >
                {#each pages as { page, title_overwrite, column: pageColumn }}
                  <a
                    class="block font-light hover:font-normal"
                    href={linkResolver(page._meta)}
                    >{title_overwrite || page?.title?.[0]?.text}</a
                  >
                {/each}
              </div>
            {/each}
          </div>
        </div>
      </div>
    {/if}
  </header>
  <main class="relative">
    <slot />
  </main>

  {#if global.show && path === "/"}
    <div
      transition:fade|global
      class="lg:fixed right-0 top-32 p-8 dark lg:rounded lg:text-right lg:shadow z-40"
    >
      <div>
        <Headlines
          headlines={{
            headline: global.headline[0],
            subline: global.subline[0],
          }}
          white={true}
        />
      </div>
      <a
        href={global.link.url || linkResolver(global.link._meta)}
        target={global.link.target}
        class="btn mt-4">{global.link_text || global.link.title}</a
      >
    </div>
  {/if}
  <footer class="bg-darkgray px-8 py-16 text-gray mt-auto text-sm">
    <div class="max-w-content mx-auto">
      <div class="lg:flex flex-wrap -m-4 justify-between items-end">
        <a href="/" class="block p-4">
          <img src="/logo-footer.svg" class="block w-20" alt="Hertz6 Logo" />
        </a>
        <div class="p-4">
          <nav class="flex mb-2 font-display text-2xl">
            {#each global.nav_footer as { page }, index}
              <a
                sveltekit:prefetch
                class={linkResolver(page._meta)}
                href={linkResolver(page._meta)}>{page?.title?.[0]?.text}</a
              >
              {#if index + 1 < global.nav_footer.length}<span class="mx-2"
                  >|</span
                >{/if}
            {/each}
          </nav>
          <div class="lg:h-12 flex flex-col justify-end">
            <Text text={global.footer} />
          </div>
        </div>
        <div
          class="p-4 flex flex-col justify-end flex-end lg:text-right ml-auto"
        >
          <div class="mb-2 font-display text-2xl">Lass Freunde bleiben:</div>
          <div class="flex social lg:justify-end items-end lg:h-12 -ml-2">
            {#if global.facebook}
              <a href={global.facebook.url} target="_blank"
                ><Icon sprite="brands" icon="facebook-f" /></a
              >
            {/if}
            {#if global.instagram}
              <a href={global.instagram.url} target="_blank"
                ><Icon sprite="brands" icon="instagram" /></a
              >
            {/if}
            {#if global.youtube}
              <a href={global.youtube.url} target="_blank"
                ><Icon sprite="brands" icon="youtube" /></a
              >
            {/if}
            {#if global.linkedin}
              <a href={global.linkedin.url} target="_blank"
                ><Icon sprite="brands" icon="linkedin-in" /></a
              >
            {/if}
            {#if global.vimeo}
              <a href={global.vimeo.url} target="_blank"
                ><Icon sprite="brands" icon="vimeo-v" /></a
              >
            {/if}
          </div>
        </div>
      </div>
    </div>
  </footer>
  {#if !dnt && consent === null}
    <div
      in:fade|global
      out:fade|global
      class="dark p-4 fixed inset-x-8 bottom-8 mx-auto z-50 max-w-lg rounded-lg text-center shadow"
    >
      <Text text={global.consent} />
      <div class="flex justify-center items-center mt-4">
        <button class="mx-2 font-medium" on:click={() => cookies(false)}
          >Ablehnen</button
        >
        <button class="btn ml-4" on:click={() => cookies(true)}>Annehmen</button
        >
      </div>
    </div>
  {/if}
</div>

<style>
  .social a {
    @apply block ml-2 transition;
    &:hover {
      @apply text-pink;
    }
  }
  .nav-button {
    font-size: 40px;
    height: 1em;
    width: 1em;
    &:before,
    &:after,
    & span {
      content: "";
      height: 4px;
      @apply block absolute m-auto w-full rounded bg-current transition;
    }
    &:before {
      transform: translateY(-0.3em);
    }
    &:after {
      transform: translateY(0.3em);
    }
    &.active {
      &:before {
        transform: rotate(135deg);
      }
      &:after {
        transform: rotate(-135deg);
      }
      & span {
        transform: translateX(100%);
      }
    }
  }
</style>
